import { Form, Spinner, Table as BootstrapTable } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { AddProps, Filter, Option } from '../models/table-models';
import {
    addInvoices,
    getInvoicePreview,
    useGetBranchPaymentInfo,
    useGetClientBranches,
    useGetInvoices,
} from '../utils/clients-hooks';
import Select from 'react-select';
import React, { Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../components/CustomToastContent';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';
import { fetchTasksAll } from '../utils/dashboard-hooks';
import { NewRequestContext } from '../context/NewRequestContext';
import { NewRequestTableRow } from '../components/NewRequest/NewRequestTableRow';
import { DatePickerWrapper } from '../components/DatePickerWrapper';
import { BranchPaymentInfo } from '../models/dashboard-models';
import { useGetAllTurnovers } from '../utils/users-hooks';
import dateFormat from 'dateformat';

export const AddInvoice = ({
    setShowModal,
    refetchEntitiesHook,
    setCompleteButtonDisabled,
    setIsLoading,
}: AddProps) => {
    const { selectedBranch } = useContext(NewRequestContext);
    const [branchId, setBranchId] = useState<number | null | undefined>(selectedBranch?.id);

    const [readBranchPaymentInfo, getBranchPaymentInfo] = useAsyncResource(useGetBranchPaymentInfo);

    const [filter, setFilter] = useState<Filter>({ tags: [], search: '', before: '', after: '' });

    const paymentInfo = readBranchPaymentInfo();

    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });

    const { control, handleSubmit, reset, watch, setValue } = useForm<BranchPaymentInfo>({
        defaultValues: useMemo(() => {
            return { ...paymentInfo, paymentType: 'INVOICE' };
        }, [paymentInfo]),
    });

    const useSubmitHandlerAdd = async (formValue: BranchPaymentInfo) => {
        setIsLoading && setIsLoading(true);
        const fee = parseFloat(watch('total')?.toString().replace(',', '.') || '0');

        // Задължението идва от офиса - вдига се флагът, за да не фактурира обслужването същия период
        // втори път (hasOfficeInvoiceCovering в client-service).
        await addInvoices(formValue?.id, { ...formValue, total: fee }, filter, true);
        refetchEntitiesHook();
        setShowModal(false);
        toast.success(<CustomToastContent content={[t('InvoiceCreated')]} />, toastOptionsTopRight);
        resourceCache(fetchTasksAll).clear();
        resourceCache(useGetInvoices).clear();
        resourceCache(useGetAllTurnovers).clear();
        resourceCache(getInvoicePreview).clear();
        setIsLoading && setIsLoading(false);
    };

    useEffect(() => {
        if (branchId != null) {
            getBranchPaymentInfo(branchId);
        }
    }, [branchId]);

    useEffect(() => {
        reset({ ...paymentInfo, paymentType: 'INVOICE' });
        setValue('invoicingMonths', 1);
        setFilter(getDefaultInvoiceFilter(paymentInfo));
    }, [paymentInfo]);

    const onChange = (value: string, field: string) => {
        if (value === 'cleared') value = '';
        setFilter({ ...filter, [field]: value });
    };

    const paymentMethods = ['CASH', 'BANK'];
    const paymentTypes = ['INVOICE'];
    const fromDateMaxDate = filter.before;

    return (
        <Form
            id='form'
            className='d-flex flex-column p-4  w-100'
            noValidate
            onSubmit={handleSubmit(useSubmitHandlerAdd)}
        >
            {!selectedBranch && (
                <div>
                    <div className='w-100 d-inline-block m-0 py-1'>{t('forBranch')}</div>
                    <Controller
                        name='id'
                        {...{ control }}
                        render={({ field }) => (
                            <Suspense
                                fallback={
                                    <div>
                                        <Spinner animation='border' /> {t('LoadingBranches')}
                                    </div>
                                }
                            >
                                <BranchSelect setBranchId={setBranchId} field={field} />
                            </Suspense>
                        )}
                    ></Controller>
                </div>
            )}
            {paymentInfo != null && (
                <BootstrapTable className='w-100 position-relative'>
                    <thead>
                        <tr className='position-relative'>
                            <th colSpan={2} className='text-center border-top fw-bold'>
                                {t('payment information')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className='d-flex flex-column'>
                        <NewRequestTableRow
                            id='fromDate'
                            label={t('fromDate')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <div className={'w-50'}>
                                <DatePickerWrapper
                                    isClearable
                                    maxDate={fromDateMaxDate}
                                    selected={filter.after}
                                    onChange={(value) => onChange(value === 'cleared' ? '' : value, 'after')}
                                />
                            </div>
                            {!filter.after && <label className='text-danger'>{t('required')}</label>}
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='toDate'
                            label={t('toDate')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <div className={'w-50'}>
                                <DatePickerWrapper
                                    isClearable
                                    minDate={filter.after}
                                    selected={filter.before}
                                    onChange={(value) => onChange(value === 'cleared' ? '' : value, 'before')}
                                />
                            </div>
                            {!filter.before && <label className='text-danger'>{t('required')}</label>}
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='paymentType'
                            label={t('paymentType')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='paymentType'
                                defaultValue='INVOICE'
                                {...{ control }}
                                render={({ field }) => (
                                    <>
                                        {paymentTypes.map((value, index) => (
                                            <Form.Check
                                                key={index}
                                                className='p-0 d-flex justify-content-center align-items-center  px-1'
                                            >
                                                <Form.Check.Input
                                                    id={`paymentTypeInvoice${index}`}
                                                    disabled={false}
                                                    type='radio'
                                                    className='m-0 '
                                                    checked={value === field.value}
                                                    {...field}
                                                    {...{ value }}
                                                />
                                                <Form.Check.Label
                                                    htmlFor={`paymentTypeInvoice${index}`}
                                                    className='px-1'
                                                >
                                                    {t(value)}
                                                </Form.Check.Label>
                                            </Form.Check>
                                        ))}
                                    </>
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='paymentMethod'
                            label={t('paymentMethod')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='paymentMethod'
                                {...{ control }}
                                render={({ field }) => (
                                    <>
                                        {paymentMethods.map((value, index) => (
                                            <Form.Check
                                                key={index}
                                                className='p-0 d-flex justify-content-center align-items-center  px-1'
                                            >
                                                <Form.Check.Input
                                                    id={`paymentMethodInvoice${index}`}
                                                    type='radio'
                                                    className='m-0 '
                                                    checked={value === field.value}
                                                    {...field}
                                                    {...{ value }}
                                                />
                                                <Form.Check.Label
                                                    htmlFor={`paymentMethodInvoice${index}`}
                                                    className='px-1'
                                                >
                                                    {t(value)}
                                                </Form.Check.Label>
                                            </Form.Check>
                                        ))}
                                    </>
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='total'
                            label={t('total')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='total'
                                {...{ control }}
                                render={({ field }) => (
                                    <>
                                        <Form.Control
                                            type='currency'
                                            className='w-30 text-center p-0'
                                            value={field.value ? field.value.toString() : ''}
                                            onChange={field.onChange}
                                            name={field.name}
                                            onBlur={field.onBlur}
                                            ref={field.ref}
                                        />
                                        <span className='ps-2'>{paymentInfo?.currency}</span>
                                    </>
                                )}
                            />
                        </NewRequestTableRow>
                    </tbody>
                </BootstrapTable>
            )}
        </Form>
    );
};

const BranchSelect: React.FC<{ setBranchId: (id: number | null) => void; field: any }> = ({ setBranchId, field }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [getBranchOptions] = useAsyncResource(useGetClientBranches, []);
    const branchOptions = getBranchOptions();

    const memoizedOptions = useMemo(
        () =>
            branchOptions.flatMap(({ companyName, eik, branchList }) =>
                branchList.map(({ id, branchName }) => ({
                    name: branchName,
                    value: String(id),
                    customSearch: `${companyName} ${branchName} ${eik}`,
                }))
            ),
        [branchOptions]
    );

    return (
        <Select<Option>
            className='w-100'
            isClearable
            onChange={(e: Option | null) => {
                field.onChange(e?.value);
                if (e?.value !== undefined) {
                    setBranchId(parseInt(e.value));
                } else {
                    setBranchId(null);
                }
            }}
            placeholder={t('enterNameOrEik')}
            maxMenuHeight={150}
            getOptionValue={({ value }) => `${value}`}
            options={memoizedOptions}
            getOptionLabel={({ name }) => name}
            filterOption={(option, inputValue) => {
                if (!inputValue) return false;
                if (option.data.customSearch === undefined) return false;
                return option.data.customSearch.toLowerCase().includes(inputValue.toLowerCase());
            }}
        ></Select>
    );
};

const getDefaultInvoiceFilter = (paymentInfo?: BranchPaymentInfo): Filter => {
    if (!paymentInfo?.paymentDate) {
        return { tags: [], search: '', before: '', after: '' };
    }

    const fromDate = parseLocalDate(paymentInfo.paymentDate);
    const months = paymentInfo.invoicingMonths && paymentInfo.invoicingMonths > 0 ? paymentInfo.invoicingMonths : 1;
    const duePaymentDate = new Date(fromDate);
    duePaymentDate.setMonth(duePaymentDate.getMonth() + months);
    duePaymentDate.setDate(duePaymentDate.getDate() - 1);

    return {
        tags: [],
        search: '',
        after: dateFormat(fromDate, 'yyyy-mm-dd'),
        before: dateFormat(duePaymentDate, 'yyyy-mm-dd'),
    };
};

const parseLocalDate = (value: string) => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
};
